import type { LocationQuery } from 'vue-router'
import client from './client'
import { currentLocale } from '@/i18n'
import type { PlatformPage } from '@/types/analytics'

export interface PageViewPayload {
  type: 'page_view.event_page' | 'page_view.registration_form'
  org_slug: string
  event_slug: string
  race_slug?: string
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const
const UTM_MAX_LENGTH = 100

// The referrer is only meaningful for the first page view of the document: in a
// single-page app document.referrer stays the entry referrer for every later navigation.
let firstPageViewSent = false

function referrerHost(): string | null {
  if (document.referrer === '') {
    return null
  }
  try {
    const host = new URL(document.referrer).hostname
    return host === '' ? null : host
  } catch {
    return null
  }
}

function firstQueryValue(value: LocationQuery[string] | undefined): string | null {
  const candidate = Array.isArray(value) ? value[0] : value
  return typeof candidate === 'string' && candidate !== '' ? candidate : null
}

/**
 * Shared attribution for every SPA page view: referrer host (first view of the document
 * only, one flag for all page-view types), UTM values from the query and the UI locale.
 */
function buildAttribution(query: LocationQuery): Record<string, string> {
  const attribution: Record<string, string> = {}

  if (!firstPageViewSent) {
    firstPageViewSent = true
    const host = referrerHost()
    if (host !== null) {
      attribution['referrer_host'] = host
    }
  }

  for (const key of UTM_KEYS) {
    const value = firstQueryValue(query[key])
    if (value !== null) {
      attribution[key] = value.slice(0, UTM_MAX_LENGTH)
    }
  }

  attribution['locale'] = currentLocale()

  return attribution
}

function isNonHumanContext(): boolean {
  return navigator.webdriver === true || (document.visibilityState as string) === 'prerender'
}

/**
 * Record a public page view. Fire-and-forget: never awaited by callers, never
 * throws, and every failure (network error, 419, 422, 429, 500) is swallowed.
 * Uses the shared axios client so the session cookie and X-XSRF-TOKEN header are sent.
 */
export function sendPageView(payload: PageViewPayload, query: LocationQuery): void {
  try {
    if (isNonHumanContext()) {
      return
    }

    const body: Record<string, string> = { ...payload, ...buildAttribution(query) }

    client.post('/public/analytics/page-views', body).catch(() => undefined)
  } catch {
    // Analytics must never affect the page.
  }
}

function hasXsrfCookie(): boolean {
  return document.cookie.split('; ').some((entry) => entry.split('=')[0] === 'XSRF-TOKEN')
}

/**
 * Resolve true once the XSRF-TOKEN cookie exists, false after the timeout. Only reads the
 * cookie set by the SPA's own functional requests; never writes a cookie. Never rejects.
 */
export function waitForXsrfCookie(timeoutMs = 10_000, intervalMs = 250): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      if (hasXsrfCookie()) {
        resolve(true)
        return
      }
      let elapsed = 0
      const timer = setInterval(() => {
        elapsed += intervalMs
        let found = false
        try {
          found = hasXsrfCookie()
        } catch {
          found = false
        }
        if (found || elapsed >= timeoutMs) {
          clearInterval(timer)
          resolve(found)
        }
      }, intervalMs)
    } catch {
      resolve(false)
    }
  })
}

/**
 * Record a view of a startline.be platform page. Fire-and-forget like sendPageView, but it
 * waits for the existing XSRF-TOKEN cookie first and drops the view when it never appears.
 * Never sends tenant slugs or any user information.
 */
export function sendPlatformPageView(
  page: PlatformPage,
  path: string | null,
  query: LocationQuery,
): void {
  try {
    if (isNonHumanContext()) {
      return
    }

    const body: Record<string, string> = {
      type: 'page_view.platform_page',
      page,
      ...(path !== null ? { path } : {}),
      ...buildAttribution(query),
    }

    void waitForXsrfCookie()
      .then((ready) => (ready ? client.post('/public/analytics/page-views', body) : undefined))
      .catch(() => undefined)
  } catch {
    // Analytics must never affect the page.
  }
}
